import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NbDialogService, NbIconLibraries } from "@nebular/theme";
import { replace, remove, clone, find } from "lodash";

import { User } from "../../@core/data/users";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";

import Bookmark from "../../models/bookmark.model";
import Course from "../../models/course.model";
import LectureContent from "../../models/lecture-content.model";
import QnA from "../../models/qna.model";
import Quiz from "../../models/quiz.model";

import { BookmarkService } from "../../services/bookmark.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import Exercise from "../../models/exercise.model";
import Announcement from "../../models/announcement.model";

@Component({
  selector: "ngx-button-bookmark",
  styleUrls: ["./button-bookmark.component.scss"],
  templateUrl: "./button-bookmark.component.html",
})
export class ButtonBookmarkComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  @Input() course? = new Course();
  @Input() lecture? = new LectureContent();
  @Input() exercise? = new Exercise();
  @Input() quiz? = new Quiz();
  @Input() qna? = new QnA();
  @Input() announcement? = new Announcement();

  @Output() onClose: EventEmitter<any> = new EventEmitter();

  submitted = false;
  loading = false;

  bookmarked?: Bookmark = new Bookmark();
  uid = "anonymous";
  isLogin = false;

  constructor(
    public userService: UserService,
    private bookmarkService: BookmarkService,
    private dialogService: NbDialogService
  ) {}

  ngOnInit() {
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.isLogin = true;
          this.getMyBookmark();
        } else {
          this.uid = "anonymous";
          this.isLogin = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  bookmark() {
    this.bookmarked.mode = this.bookmarked.mode ? this.bookmarked.mode : "";
    this.bookmarked.refId = this.bookmarked.refId ? this.bookmarked.refId : "";
    this.bookmarked.refTitle = this.bookmarked.refTitle
      ? this.bookmarked.refTitle
      : "";
    this.bookmarked.refDescription = this.bookmarked.refDescription
      ? this.bookmarked.refDescription
      : "";
    this.bookmarked.refUid = this.bookmarked.refUid
      ? this.bookmarked.refUid
      : "";
    this.bookmarked.refUrl = this.bookmarked.refUrl
      ? this.bookmarked.refUrl
      : "";

    this.bookmarked.value = this.bookmarked.value
      ? this.bookmarked.value
      : false;

    if (this.bookmarked && this.bookmarked.id && this.bookmarked.id != "") {
      this.updateBookmark();
    } else {
      this.createBookmark();
    }
  }

  updateBookmark() {
    console.log("updateBookmark");
    this.loading = true;
    this.submitted = true;

    this.bookmarked.value = this.bookmarked.value ? false : true;

    if (this.bookmarked.value) {
      this.bookmarkService
        .checkBookmark(this.bookmarked)
        .then((value) => {
          this.loading = false;
          this.submitted = false;
        })
        .catch((err) => {
          this.loading = false;
          this.submitted = false;
        });
    } else {
      this.bookmarkService
        .uncheckBookmark(this.bookmarked)
        .then((value) => {
          this.loading = false;
          this.submitted = false;
        })
        .catch((err) => {
          this.loading = false;
          this.submitted = false;
        });
    }
  }

  createBookmark() {
    this.initBookmark();

    this.loading = true;
    this.submitted = true;

    this.bookmarked.value = true;

    this.bookmarkService
      .create(this.bookmarked)
      .then((value) => {
        this.bookmarked = clone(value);

        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  private initBookmark() {
    this.bookmarked = new Bookmark();

    this.bookmarked.mode = "";
    this.bookmarked.refId = "";
    this.bookmarked.refUrl = "";
    this.bookmarked.value = false;
    if (this.course && this.course.id) {
      this.bookmarked.mode = "courses";
      this.bookmarked.refId = this.course.id;
      this.bookmarked.refTitle = this.course.title;
      this.bookmarked.refDescription = this.course.description;
      this.bookmarked.refUid = this.course.createdBy;
      this.bookmarked.refAuthor = this.course.author;
      this.bookmarked.refUrl = `/course/${this.course.id}`;
    } else if (this.lecture && this.lecture.id) {
      this.bookmarked.mode = "lecture-contents";
      this.bookmarked.refId = this.lecture.id;
      this.bookmarked.refTitle = this.lecture.title;
      this.bookmarked.refDescription = this.lecture.description;
      this.bookmarked.refUid = this.lecture.createdBy;
      this.bookmarked.refAuthor = this.lecture.author;
      this.bookmarked.refUrl = `/lecture/${this.lecture.id}`;
    } else if (this.exercise && this.exercise.id) {
      this.bookmarked.mode = "exercises";
      this.bookmarked.refId = this.exercise.id;
      this.bookmarked.refTitle = this.exercise.title;
      this.bookmarked.refUid = this.exercise.createdBy;
      this.bookmarked.refAuthor = this.exercise.author;
      this.bookmarked.refUrl = `/exercise/${this.exercise.id}`;
    } else if (this.qna && this.qna.id) {
      this.bookmarked.mode = "qnas";
      this.bookmarked.refId = this.qna.id;
      this.bookmarked.refTitle = this.qna.title;
      this.bookmarked.refDescription = this.qna.description;
      this.bookmarked.refUid = this.qna.createdBy;
      this.bookmarked.refAuthor = this.qna.author;
      this.bookmarked.refUrl = `/qna/${this.qna.id}`;
    } else if (this.quiz && this.quiz.id) {
      this.bookmarked.mode = "quizzes";
      this.bookmarked.refId = this.quiz.id;
      this.bookmarked.refTitle = this.quiz.title;
      this.bookmarked.refDescription = "";
      this.bookmarked.refUid = this.quiz.createdBy;
      this.bookmarked.refAuthor = this.quiz.author;
      this.bookmarked.refUrl = `/quiz/${this.quiz.id}`;
    } else if (this.announcement && this.announcement.id) {
      this.bookmarked.mode = "announcements";
      this.bookmarked.refId = this.announcement.id;
      this.bookmarked.refTitle = this.announcement.title;
      this.bookmarked.refDescription = this.announcement.description;
      this.bookmarked.refUid = this.announcement.createdBy;
      this.bookmarked.refAuthor = this.announcement.author;
      this.bookmarked.refUrl = `/announcement/${this.announcement.id}`;
    }
  }

  private getMyBookmark(): void {
    this.initBookmark();

    if (
      this.bookmarked.mode != "" &&
      this.bookmarked.refId != "" &&
      this.uid != "" &&
      this.bookmarked.refUid != this.uid
    ) {
      this.loading = true;

      this.bookmarkService
        .getMyBookmark(this.bookmarked.mode, this.bookmarked.refId, this.uid)
        .then((querySnapshot) => {
          if (querySnapshot.docs.length > 0) {
            querySnapshot.forEach((doc) => {
              let data = new Bookmark();
              data = doc.data();

              this.bookmarked.id = doc.id;
              this.bookmarked.value = data.value;
            });
          }
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    }
  }
}
